import { useContext, useEffect } from 'react';
import { UNSAFE_NavigationContext as NavigationContext } from 'react-router-dom';
import type { History, Transition } from 'history';

// Add support for useBlocker for react router v6
// https://reactrouter.com/en/6.4.1/upgrading/v5#prompt-is-not-currently-supported
//
// React Router v6 removed the navigator.block() API. This implementation
// uses the history library directly from the navigation context to provide
// the same blocking functionality.
export default function useBlocker(
  blocker: (tx: Transition) => void,
  when = true,
) {
  const navigationContext = useContext(NavigationContext);

  useEffect(() => {
    if (!when || !navigationContext) {
      return;
    }

    // The history object is available via navigator in React Router v6
    const history = navigationContext.navigator as History;

    // Check if history.block is available (it should be from the history library)
    if (typeof history.block !== 'function') {
      // If block is not available, we can't block navigation
      // This is a fallback - the blocker won't work but won't crash
      return;
    }

    const unblock = history.block((tx) => {
      const autoUnblockingTx = {
        ...tx,
        retry() {
          unblock();
          tx.retry();
        },
      };

      blocker(autoUnblockingTx);
    });

    return unblock;
  }, [navigationContext, blocker, when]);
}
